import { creditsQueryOptions } from "@/api/credits.api";
import { Button } from "@/components/generic/Button";
import { Separator } from "@/components/generic/Separator";
import CreditsUsage from "@/components/settings/CreditsUsage";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import {
  essentialsCreditsText,
  essentialsText,
} from "@/hooks/use-trial-period";
import { solutionVersionPlanMap, useAuth } from "@/services/auth.service";
import { formatMoney } from "@/utils/money";
import { formatNumber } from "@/utils/number-utils";
import {
  getCurrentContract,
  getLicenseData,
} from "@/utils/organization-helpers";
import { promiseTimeout } from "@/utils/promise-timeout";
import { pluralize } from "@/utils/string-utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/subscription/_subscription/",
)({
  component: RouteComponent,
  loader: async ({ context }) => {
    const creditsData =
      await context.queryClient.ensureQueryData(creditsQueryOptions);
    return {
      useCreditsQuery: () =>
        useQuery({ ...creditsQueryOptions, initialData: creditsData }),
    };
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const context = Route.useRouteContext();
  const { useCreditsQuery } = Route.useLoaderData();

  const { user } = useAuth();

  const [showTrialCTAModal, setShowTrialCTAModal] = useState(false);

  const creditsQuery = useCreditsQuery();

  const contract = getCurrentContract(user.organization);
  const { currentLicenses, licensePrice } = getLicenseData(user.organization);

  const currentSolution = useMemo(
    () =>
      `${context.auth.user.organization.currentSolution.productName} ${context.auth.user.organization.currentSolution.versionName}`,
    [context.auth.user.organization.currentSolution],
  );

  const [isUpgrading, setIsUpgrading] = useState(false);

  const currentPlan = useMemo(
    () =>
      solutionVersionPlanMap.get(
        user.organization.currentSolution.versionUniqueName,
      ) ?? "freemium",
    [user.organization.currentSolution.versionUniqueName],
  );

  const currentPlanPrice = useMemo(
    () =>
      contract?.price ?? context.appConfiguration.versions[currentPlan].price,
    [context.appConfiguration.versions, contract?.price, currentPlan],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const upgradePlan = async () => {
    try {
      setIsUpgrading(true);

      // Determine upgrade path based on current plan
      let targetPlan: "essentials" | "pro" | null = null;
      if (currentPlan === "freemium") {
        targetPlan = "essentials";
      } else if (currentPlan === "essentials") {
        targetPlan = "pro";
      }

      if (!targetPlan) {
        console.error("No valid upgrade path available");
        return;
      }

      // Call the API to upgrade
      // await api.post(`apps/sales/settings/version/upgrade/${targetPlan}`);
      promiseTimeout(2500);

      toast.success("Abonnement opgraderet", {
        description:
          "Siden vil genindlæse om et øjeblik for at opdatere dit abonnement.",
        action: {
          label: "Genindlæs nu",
          onClick: () => {
            window.location.reload();
          },
        },
      });

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      console.error("Failed to upgrade plan:", error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const credits = useMemo(() => {
    const {
      baseCreditsPerMonth,
      extraCreditsPerMonth,
      extraCreditsPerMonthPrice,
      creditsRemaining,
      creditsBuffer,
    } = creditsQuery.data;

    const totalCreditsPerMonth = baseCreditsPerMonth + extraCreditsPerMonth;
    const monthlyCreditsRemaining = creditsRemaining - creditsBuffer;

    const month = {
      base: baseCreditsPerMonth,
      extra: extraCreditsPerMonth,
      extraPrice: extraCreditsPerMonthPrice,
      total: totalCreditsPerMonth,
      remaining: monthlyCreditsRemaining,
      used: totalCreditsPerMonth - monthlyCreditsRemaining,
    };

    const total = month.total + creditsBuffer;
    const remaining = creditsRemaining;

    return {
      month,
      buffer: creditsBuffer,
      total,
      remaining,
      used: total - remaining,
    };
  }, [creditsQuery.data]);

  return (
    <div className="px-14 pt-6 pb-12">
      <div className="mx-auto flex max-w-md flex-col flex-wrap gap-y-8">
        <div className="border-theme-outline-variant relative flex w-full grow flex-col items-center justify-between rounded-sm border p-8">
          <div className="mb-4 flex h-10 w-full items-center justify-between">
            <AnimatePresence>
              {!isUpgrading && (
                <h1 className="text-xl font-semibold">Abonnement & forbrug</h1>
              )}
            </AnimatePresence>
            {/* {!isPro && (
              <Button
                className="ml-auto min-w-0"
                onClick={upgradePlan}
                {...(isUpgrading
                  ? { intent: "secondary", isDisabled: true }
                  : { intent: "primary" })}
                size="sm"
              >
                {isUpgrading ? "Opgraderer..." : "Opgrader"}
              </Button>
            )} */}
          </div>
          <div className="flex w-full flex-col gap-y-3">
            <table className="-my-4 border-separate border-spacing-y-2.5">
              <tbody className="text-sm">
                <tr className="*:font-normal [&>td]:text-right [&>th]:text-left">
                  <th>
                    {currentSolution} inkl. {formatNumber(credits.month.base)}{" "}
                    credits
                  </th>
                  <td>{formatMoney(currentPlanPrice)}</td>
                </tr>
                {currentLicenses > 1 && (
                  <tr className="*:font-normal [&>td]:text-right [&>th]:text-left">
                    <th>
                      {formatNumber(currentLicenses)}{" "}
                      {pluralize(currentLicenses, "bruger", "brugere")}
                    </th>
                    <td>{formatMoney(licensePrice * (currentLicenses - 1))}</td>
                  </tr>
                )}
                {credits.month.extra > 0 && (
                  <tr className="*:font-normal [&>td]:text-right [&>th]:text-left">
                    <th>
                      {formatNumber(credits.month.extra)}{" "}
                      {pluralize(credits.month.extra, "credit", "credits")}
                    </th>
                    <td>{formatMoney(credits.month.extraPrice)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <Separator className="my-4" />
            <CreditsUsage {...credits} />
          </div>
        </div>
        {!user.organization.currentSolution.trial && (
          <>
            <div className="border-theme-outline-variant flex w-full grow flex-col items-center justify-between rounded-sm border px-8 py-9">
              <div className="flex w-full items-center justify-between">
                <div className="mr-4 flex flex-col gap-y-2">
                  <h1 className="text-base font-semibold">Brugere</h1>
                  <span className="text-theme-on-background-variant text-sm font-medium">
                    Køb adgange til flere brugere
                  </span>
                </div>
                <Button
                  link={{ to: "/settings/subscription/add-users" }}
                  intent="primary"
                  size="lg"
                >
                  Køb brugere
                </Button>
              </div>
              <Separator className="my-7" />
              <div className="flex w-full items-center justify-between">
                <div className="text-theme-on-background-variant text-sm">
                  {formatNumber(currentLicenses - 1)} ekstra brugere
                </div>
                <div className="text-base font-medium">
                  {formatMoney(licensePrice * (currentLicenses - 1))} / måned
                </div>
              </div>
            </div>
            <div className="border-theme-outline-variant relative flex w-full grow flex-col items-center justify-between rounded-sm border px-8 py-9">
              <div className="flex w-full items-center justify-between gap-x-2">
                <div className="mr-4 flex flex-col gap-y-2">
                  <h1 className="text-base font-semibold">Credits</h1>
                  <span className="text-theme-on-background-variant text-sm font-medium">
                    Køb ekstra credits, hvis du har brug for endnu flere leads
                  </span>
                </div>
                <Button
                  onPress={() => {
                    if (
                      !context.appConfiguration.features.canUpgradeCreditTier
                    ) {
                      setShowTrialCTAModal(true);
                      return;
                    }
                    navigate({ to: "/settings/subscription/add-credits" });
                  }}
                  intent="primary"
                  size="lg"
                >
                  Køb credits
                </Button>
              </div>
              <Separator className="my-7" />
              <div className="flex w-full items-center justify-between">
                <div className="text-theme-on-background-variant text-sm">
                  {formatNumber(credits.month.extra)} ekstra credits
                </div>
                <div className="text-base font-medium">
                  {formatMoney(credits.month.extraPrice)} / måned
                </div>
              </div>
            </div>
          </>
        )}
        <div className="text-theme-on-background-variant flex flex-col items-center justify-center gap-y-2 text-[11px]">
          <span className="font-medium">
            Ønsker du at ændre eller opsige dit abonnement?
          </span>
          <span className="text-center">
            Kontakt os på telefon <a href="tel:+4571747812">71 74 78 12</a>{" "}
            eller send en mail til{" "}
            <a href="mailto:contact@lassox.com">contact@lassox.com</a>
            <br />
            Vi hjælper dig hurtigt og nemt.
          </span>
        </div>
      </div>
      <TrialCTAModal
        isOpen={showTrialCTAModal}
        onOpenChange={setShowTrialCTAModal}
        title={essentialsCreditsText}
      />
    </div>
  );
}
